import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  IconButton,
  Tabs,
  Tab
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import dynamicContentService from '@/services/dynamicContentService';

interface FAQCategory {
  id: string;
  name: string;
  slug: string;
  description: string;
  icon: string;
  displayOrder: number;
  isActive: boolean;
  faqCount: number;
}

interface FAQ {
  id: string;
  categoryId: string;
  question: string;
  answer: string;
  answerFormat: string;
  displayOrder: number;
  isActive: boolean;
  viewsCount: number;
  helpfulCount: number;
  notHelpfulCount: number;
  tags: string[];
  category: {
    name: string;
    slug: string;
  };
}

const FAQManagementPage: React.FC = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [categories, setCategories] = useState<FAQCategory[]>([]);
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Category dialog
  const [categoryDialog, setCategoryDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState<FAQCategory | null>(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    slug: '',
    description: '',
    icon: '',
    display_order: 0
  });

  // FAQ dialog
  const [faqDialog, setFaqDialog] = useState(false);
  const [editingFaq, setEditingFaq] = useState<FAQ | null>(null);
  const [faqForm, setFaqForm] = useState({
    category_id: '',
    question: '',
    answer: '',
    answer_format: 'html',
    display_order: 0,
    tags: [] as string[]
  });

  useEffect(() => {
    fetchCategories();
    fetchFaqs();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await dynamicContentService.getFAQCategories();

      if (result.success) {
        setCategories(result.data);
      } else {
        setError(result.error || 'Failed to fetch FAQ categories');
      }
    } catch (err: any) {
      console.error('Error fetching FAQ categories:', err);
      setError(err.response?.data?.error || 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchFaqs = async () => {
    try {
      const result = await dynamicContentService.getFAQs();

      if (result.success) {
        setFaqs(result.data);
      }
    } catch (err: any) {
      console.error('Error fetching FAQs:', err);
      setError(err.response?.data?.error || 'Failed to fetch FAQs');
    }
  };

  const handleOpenCategoryDialog = (category?: FAQCategory) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({
        name: category.name,
        slug: category.slug,
        description: category.description,
        icon: category.icon,
        display_order: category.displayOrder
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({
        name: '',
        slug: '',
        description: '',
        icon: '',
        display_order: 0
      });
    }
    setCategoryDialog(true);
  };

  const handleSaveCategory = async () => {
    setError(null);

    try {
      const result = editingCategory
        ? await dynamicContentService.updateFAQCategory(editingCategory.id, categoryForm)
        : await dynamicContentService.createFAQCategory(categoryForm);

      if (result.success) {
        setSuccess(editingCategory ? 'Category updated' : 'Category created');
        setTimeout(() => setSuccess(null), 3000);
        setCategoryDialog(false);
        fetchCategories();
      } else {
        setError(result.error || 'Failed to save category');
      }
    } catch (err: any) {
      console.error('Error saving category:', err);
      setError(err.response?.data?.error || 'Network error. Please try again.');
    }
  };

  const handleOpenFaqDialog = (faq?: FAQ) => {
    if (faq) {

      // Verify the category exists in the categories list
      const categoryExists = categories.find(c => c.id === faq.categoryId);
      if (!categoryExists) {
      } else {
      }

      setEditingFaq(faq);
      setFaqForm({
        category_id: faq.categoryId || '',
        question: faq.question || '',
        answer: faq.answer || '',
        answer_format: faq.answerFormat || 'html',
        display_order: faq.displayOrder || 0,
        tags: faq.tags || []
      });
    } else {
      setEditingFaq(null);
      setFaqForm({
        category_id: categories[0]?.id || '',
        question: '',
        answer: '',
        answer_format: 'html',
        display_order: 0,
        tags: []
      });
    }
    setFaqDialog(true);
  };

  const handleSaveFaq = async () => {
    setError(null);

    // Validation
    if (!faqForm.category_id) {
      setError('Please select a category');
      return;
    }

    if (!faqForm.question || !faqForm.answer) {
      setError('Question and answer are required');
      return;
    }


    try {
      const result = editingFaq
        ? await dynamicContentService.updateFAQ(editingFaq.id, faqForm)
        : await dynamicContentService.createFAQ(faqForm);


      if (result.success) {
        setSuccess(editingFaq ? 'FAQ updated' : 'FAQ created');
        setTimeout(() => setSuccess(null), 3000);
        setFaqDialog(false);
        fetchFaqs();
      } else {
        setError(result.error || 'Failed to save FAQ');
      }
    } catch (err: any) {
      console.error('Error saving FAQ:', err);
      setError(err.response?.data?.error || err.message || 'Network error. Please try again.');
    }
  };

  const handleDeleteFaq = async (faqId: string) => {
    if (!confirm('Are you sure you want to delete this FAQ?')) return;

    try {
      const result = await dynamicContentService.deleteFAQ(faqId);

      if (result.success) {
        setSuccess('FAQ deleted');
        setTimeout(() => setSuccess(null), 3000);
        fetchFaqs();
      } else {
        setError(result.error || 'Failed to delete FAQ');
      }
    } catch (err: any) {
      console.error('Error deleting FAQ:', err);
      setError(err.response?.data?.error || 'Network error. Please try again.');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        FAQ Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card sx={{ mb: 2 }}>
        <Tabs value={tabIndex} onChange={(e, v) => setTabIndex(v)}>
          <Tab label="Categories" />
          <Tab label="FAQs" />
        </Tabs>
      </Card>

      {/* Categories Tab */}
      {tabIndex === 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenCategoryDialog()}
            >
              Add Category
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Slug</TableCell>
                  <TableCell>Icon</TableCell>
                  <TableCell>Order</TableCell>
                  <TableCell>FAQs</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.map((cat) => (
                  <TableRow key={cat.id}>
                    <TableCell>{cat.name}</TableCell>
                    <TableCell>{cat.slug}</TableCell>
                    <TableCell>{cat.icon}</TableCell>
                    <TableCell>{cat.displayOrder}</TableCell>
                    <TableCell>{cat.faqCount}</TableCell>
                    <TableCell>
                      <Chip
                        label={cat.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={cat.isActive ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpenCategoryDialog(cat)}>
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* FAQs Tab */}
      {tabIndex === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenFaqDialog()}
            >
              Add FAQ
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Question</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Views</TableCell>
                  <TableCell>Helpful</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {faqs.map((faq) => (
                  <TableRow key={faq.id}>
                    <TableCell>{faq.question}</TableCell>
                    <TableCell>
                      <Chip label={faq.category.name} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>{faq.viewsCount}</TableCell>
                    <TableCell>{faq.helpfulCount}</TableCell>
                    <TableCell>
                      <Chip
                        label={faq.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={faq.isActive ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpenFaqDialog(faq)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteFaq(faq.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Category Dialog */}
      <Dialog open={categoryDialog} onClose={() => setCategoryDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCategory ? 'Edit Category' : 'Add Category'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              value={categoryForm.name}
              onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
            />
            <TextField
              fullWidth
              label="Slug"
              value={categoryForm.slug}
              onChange={(e) => setCategoryForm({ ...categoryForm, slug: e.target.value })}
            />
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={2}
              value={categoryForm.description}
              onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
            />
            <TextField
              fullWidth
              label="Icon (emoji or name)"
              value={categoryForm.icon}
              onChange={(e) => setCategoryForm({ ...categoryForm, icon: e.target.value })}
            />
            <TextField
              fullWidth
              label="Display Order"
              type="number"
              value={categoryForm.display_order}
              onChange={(e) => setCategoryForm({ ...categoryForm, display_order: parseInt(e.target.value) })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveCategory} variant="contained">
            {editingCategory ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* FAQ Dialog */}
      <Dialog open={faqDialog} onClose={() => setFaqDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingFaq ? 'Edit FAQ' : 'Add FAQ'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl fullWidth required>
              <InputLabel>Category</InputLabel>
              <Select
                value={faqForm.category_id || ''}
                label="Category"
                onChange={(e) => setFaqForm({ ...faqForm, category_id: e.target.value })}
                displayEmpty={false}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Question"
              value={faqForm.question}
              onChange={(e) => setFaqForm({ ...faqForm, question: e.target.value })}
            />

            <TextField
              fullWidth
              label="Answer"
              multiline
              rows={8}
              value={faqForm.answer}
              onChange={(e) => setFaqForm({ ...faqForm, answer: e.target.value })}
            />

            <TextField
              fullWidth
              label="Display Order"
              type="number"
              value={faqForm.display_order}
              onChange={(e) => setFaqForm({ ...faqForm, display_order: parseInt(e.target.value) })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFaqDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveFaq} variant="contained">
            {editingFaq ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FAQManagementPage;
